import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { categoryIcon } from '@/entities/category';
import { FavoriteStar } from '@/features/favorites';
import { Card } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import styles from './CatalogTile.module.css';

const TONES = ['info', 'positive', 'accent', 'review', 'pending', 'warning'] as const;

function toneOf(seed: string): (typeof TONES)[number] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return TONES[h % TONES.length];
}

interface CategoryGlyphProps {
  icon?: string | null | LucideIcon;
  seed?: string;
  size?: 'md' | 'lg';
  className?: string;
}

export const CategoryGlyph: React.FC<CategoryGlyphProps> = ({ icon, seed, size = 'md', className }) => {
  const Icon = typeof icon === 'string' || icon == null ? categoryIcon(icon) : icon;
  return (
    <span
      className={cn(styles.glyph, size === 'lg' ? styles.glyphLg : styles.glyphMd, className)}
      data-tone={seed ? toneOf(seed) : 'neutral'}
      aria-hidden
    >
      {/* eslint-disable-next-line react-hooks/static-components -- categoryIcon достаёт готовый компонент из статичной карты, идентичность стабильна */}
      <Icon size={size === 'lg' ? 26 : 20} strokeWidth={1.75} />
    </span>
  );
};

interface CatalogTileProps {
  to: string;
  title: ReactNode;
  titleAs?: 'h2' | 'h3';
  meta?: ReactNode;
  description?: ReactNode;
  leading?: ReactNode;
  favorite?: { active: boolean; onToggle: () => void };
  className?: string;
}

export const CatalogTile: React.FC<CatalogTileProps> = ({
  to,
  title,
  titleAs: Title = 'h3',
  meta,
  description,
  leading,
  favorite,
  className,
}) => (
  <Card padded={false} className={cn(styles.item, favorite && styles.withAction, className)}>
    <Link to={to} className={styles.link}>
      {leading}
      <div className={styles.main}>
        <Title className={styles.title}>{title}</Title>
        {meta && <span className={styles.meta}>{meta}</span>}
        {description && <p className={styles.description}>{description}</p>}
      </div>
      <ChevronRight className={styles.chevron} size={18} aria-hidden />
    </Link>
    {favorite && (
      <div className={styles.action}>
        <FavoriteStar active={favorite.active} onToggle={favorite.onToggle} size={16} />
      </div>
    )}
  </Card>
);
